import { prepareContent } from '@/lib/message';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

import type { IMessageElement, IStep } from '@chainlit/react-client';

import { CURSOR_PLACEHOLDER } from '@/components/BlinkingCursor';
import { Markdown } from '@/components/Markdown';
import { SourcesAccordion } from '@/components/SourcesAccordion';

import { InlinedElements } from './InlinedElements';

interface StreamingConfig {
  enabled?: boolean;
  intervalMs?: number;
  interval_ms?: number;
  initialDelayMs?: number;
  initial_delay_ms?: number;
  strategy?: string;
}

const STREAMING_METADATA_KEYS = [
  'frontend_streaming',
  'frontendStream',
  'rag_streaming',
  'ragStreaming'
] as const;

const extractStreamingConfig = (
  metadata?: IStep['metadata']
): StreamingConfig | undefined => {
  if (!metadata || typeof metadata !== 'object') {
    return undefined;
  }

  const record = metadata as Record<string, unknown>;

  for (const key of STREAMING_METADATA_KEYS) {
    const candidate = record[key as keyof typeof record];
    if (candidate && typeof candidate === 'object') {
      return candidate as StreamingConfig;
    }
  }

  const uiHints = record['ui_hints'];
  if (uiHints && typeof uiHints === 'object') {
    const hintsRecord = uiHints as Record<string, unknown>;
    for (const key of STREAMING_METADATA_KEYS) {
      const candidate = hintsRecord[key as keyof typeof hintsRecord];
      if (candidate && typeof candidate === 'object') {
        return candidate as StreamingConfig;
      }
    }
  }

  return undefined;
};

const extractSourcesFromContent = (text: string): string[] => {
  if (!text) {
    return [];
  }

  const sourcesMatch = text.match(/Sources:\n((?:- .+\n?)*)/);
  if (!sourcesMatch || !sourcesMatch[1]) {
    return [];
  }

  const sourceLines = sourcesMatch[1].split('\n').filter(line => line.trim().startsWith('- '));
  return sourceLines.map(line => line.replace(/^- /, '').trim()).filter(Boolean);
};

const removeSourcesFromContent = (text: string): string => {
  if (!text) {
    return '';
  }
  return text.replace(/\n\nSources:\n((?:- .+\n?)*)/g, '').trim();
};

const splitMarkdownIntoBlocks = (text: string): string[] => {
  if (!text) {
    return [];
  }

  const segments: string[] = [];
  let start = 0;
  let insideCode = false;
  let index = 0;

  const flush = (end: number) => {
    if (end <= start) {
      return;
    }
    segments.push(text.slice(start, end));
    start = end;
  };

  while (index < text.length) {
    if (text.startsWith('```', index)) {
      if (!insideCode) {
        flush(index);
        insideCode = true;
        index += 3;
        while (index < text.length && text[index] !== '\n') {
          index += 1;
        }
      } else {
        insideCode = false;
        index += 3;
        while (index < text.length && text[index] !== '\n') {
          index += 1;
        }
        flush(index);
      }
      continue;
    }

    if (!insideCode) {
      if (text.startsWith('\n\n', index)) {
        flush(index + 2);
        index += 2;
        continue;
      }

      if (text[index] === '\n') {
        const nextLineEnd = text.indexOf('\n', index + 1);
        const nextLine =
          nextLineEnd === -1
            ? text.slice(index + 1)
            : text.slice(index + 1, nextLineEnd);

        if (
          nextLine.trim().length === 0 ||
          /^\s*[-*+]\s+/.test(nextLine) ||
          /^\s*\d+\.\s+/.test(nextLine) ||
          nextLine.trim().startsWith('```')
        ) {
          flush(index + 1);
        }
      }
    }

    index += 1;
  }

  if (start < text.length) {
    flush(text.length);
  }

  return segments.length ? segments : [text];
};

const computeAdvanceSize = (block: string, remaining: number): number => {
  if (remaining <= 0) {
    return 0;
  }

  if (remaining <= 3) {
    return 1;
  }

  const trimmed = block.trim();
  if (!trimmed) {
    return remaining;
  }

  if (trimmed.startsWith('```') || trimmed.includes('\n```')) {
    return Math.min(remaining, Math.max(4, Math.ceil(remaining / 6)));
  }

  if (/^\s*([-*+]\s|\d+\.\s)/.test(trimmed)) {
    return Math.min(remaining, 2);
  }

  if (trimmed.length > 240) {
    return Math.min(remaining, Math.max(4, Math.ceil(trimmed.length / 80)));
  }

  if (trimmed.length > 120) {
    return Math.min(remaining, 3);
  }

  if (trimmed.length > 60) {
    return Math.min(remaining, 2);
  }

  return 1;
};

const computeStepDelay = (
  block: string,
  baseInterval: number,
  advance: number,
  cursor: number,
  atBlockEnd: boolean
): number => {
  // ** add pauses for punctuation and block ends if desired **

  // if (atBlockEnd) {
  //   const trimmed = block.trim();
  //   const terminalPause = /[.!?]$/.test(trimmed) ? 40 : 28;
  //   return Math.max(baseInterval + terminalPause, Math.min(90, baseInterval * 2 + terminalPause));
  // }

   const punctuationPause = 0
  // const prevChar = block.charAt(Math.max(0, cursor - 1));
  // const punctuationPause = /[.?!]/.test(prevChar)
  //   ? 120
  //   : /[,;:]/.test(prevChar)
  //     ? 60
  //     : 0;

  const paceFactor = advance > 2 ? 0.55 : advance > 1 ? 0.75 : 1;
  const density = Math.min(1.6, Math.max(0.8, block.length / 180));
  const delay = baseInterval * paceFactor * density + punctuationPause;
  return Math.min(180, Math.round(delay));
};

export interface Props {
  elements: IMessageElement[];
  message: IStep;
  allowHtml?: boolean;
  latex?: boolean;
  onStreamingChange?: (isStreaming: boolean) => void;
}

const MessageContent = memo(
  ({ message, elements, allowHtml, latex, onStreamingChange }: Props) => {
    const timeoutsRef = useRef<number[]>([]);
    const finalOutputRef = useRef(message.output || '');
    const hasPlayedRef = useRef(false);

    useEffect(() => {
      finalOutputRef.current = message.output || '';
    }, [message.output]);

    useEffect(() => {
      hasPlayedRef.current = false;
    }, [message.id]);

    const streamingConfig = useMemo(() => {
      const config = extractStreamingConfig(message.metadata);
      if (config?.strategy && config.strategy !== 'markdown_block') {
        return undefined;
      }
      return config;
    }, [message.metadata]);

    const createdAtRecency = useMemo(() => {
      if (!message.createdAt) {
        return true;
      }

      const createdMs = new Date(message.createdAt).getTime();
      if (Number.isNaN(createdMs)) {
        return true;
      }

      const RECENCY_WINDOW_MS = 20_000;
      return Date.now() - createdMs < RECENCY_WINDOW_MS;
    }, [message.createdAt]);

    const intervalMs =
      streamingConfig?.intervalMs ?? streamingConfig?.interval_ms ?? 90;
    const initialDelayMs =
      streamingConfig?.initialDelayMs ?? streamingConfig?.initial_delay_ms ?? 120;

    const enableFrontendStreaming = Boolean(
      streamingConfig &&
        !message.streaming &&
        message.type === 'assistant_message' &&
        (streamingConfig.enabled ?? true) &&
        createdAtRecency
    );

    const [frontendOutput, setFrontendOutput] = useState(
      () => message.output || ''
    );
    const [isFrontendStreaming, setIsFrontendStreaming] = useState(false);

    useEffect(() => {
      if (typeof window !== 'undefined') {
        timeoutsRef.current.forEach((id) => window.clearTimeout(id));
      }
      timeoutsRef.current = [];

      if (!enableFrontendStreaming) {
        hasPlayedRef.current = true;
        setIsFrontendStreaming(false);
        setFrontendOutput(finalOutputRef.current);
        return;
      }

      const finalOutput = message.output || '';
      finalOutputRef.current = finalOutput;

      if (!finalOutput) {
        setFrontendOutput('');
        setIsFrontendStreaming(false);
        return;
      }

      if (hasPlayedRef.current) {
        setFrontendOutput(finalOutput);
        setIsFrontendStreaming(false);
        return;
      }

      hasPlayedRef.current = true;

      if (typeof window === 'undefined') {
        setFrontendOutput(finalOutput);
        setIsFrontendStreaming(false);
        return;
      }

      let cancelled = false;
      const blocks = splitMarkdownIntoBlocks(finalOutput);
      let blockIndex = 0;
      let charIndex = 0;
      let aggregated = '';
      const baseInterval = Math.max(0, intervalMs);
      const startDelay = Math.max(0, initialDelayMs);

      setFrontendOutput('');
      setIsFrontendStreaming(true);

      const schedule = (delay: number, cb: () => void) => {
        if (cancelled || typeof window === 'undefined') {
          return;
        }
        const timeoutId = window.setTimeout(() => {
          timeoutsRef.current = timeoutsRef.current.filter((id) => id !== timeoutId);
          if (!cancelled) {
            cb();
          }
        }, delay);
        timeoutsRef.current.push(timeoutId);
      };

      const run = () => {
        if (cancelled) {
          return;
        }

        if (blockIndex >= blocks.length) {
          setFrontendOutput(finalOutputRef.current);
          setIsFrontendStreaming(false);
          return;
        }

        const block = blocks[blockIndex];
        const remaining = block.length - charIndex;
        const advance = computeAdvanceSize(block, remaining);

        if (advance <= 0) {
          aggregated += block;
          blockIndex += 1;
          charIndex = 0;
          schedule(0, run);
          return;
        }

        charIndex = Math.min(block.length, charIndex + advance);

        const nextOutput = aggregated + block.slice(0, charIndex);
        setFrontendOutput(nextOutput);

        const atBlockEnd = charIndex >= block.length;
        const cursorPosition = atBlockEnd ? block.length : charIndex;

        if (atBlockEnd) {
          aggregated = nextOutput;
          blockIndex += 1;
          charIndex = 0;
        }

        const delay = computeStepDelay(
          block,
          baseInterval,
          advance,
          cursorPosition,
          atBlockEnd
        );

        schedule(delay, run);
      };

      schedule(startDelay, run);

      const handleVisibilityChange = () => {
        if (typeof document === 'undefined') {
          return;
        }

        if (document.visibilityState === 'hidden') {
          cancelled = true;
          if (typeof window !== 'undefined') {
            timeoutsRef.current.forEach((id) => window.clearTimeout(id));
          }
          timeoutsRef.current = [];
          setFrontendOutput(finalOutputRef.current);
          setIsFrontendStreaming(false);
        }
      };

      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', handleVisibilityChange);
      }

      return () => {
        cancelled = true;
        if (typeof window !== 'undefined') {
          timeoutsRef.current.forEach((id) => window.clearTimeout(id));
        }
        timeoutsRef.current = [];
        if (typeof document !== 'undefined') {
          document.removeEventListener('visibilitychange', handleVisibilityChange);
        }
      };
    }, [
      enableFrontendStreaming,
      message.id,
      message.output,
      intervalMs,
      initialDelayMs
    ]);

    useEffect(() => {
      onStreamingChange?.(message.streaming || isFrontendStreaming);
    }, [isFrontendStreaming, message.streaming, onStreamingChange]);

    const resolvedOutput = message.streaming
      ? message.output || ''
      : frontendOutput;

    const isStreaming =
      message.streaming || (enableFrontendStreaming && isFrontendStreaming);

    const outputContent =
      isStreaming && resolvedOutput
        ? resolvedOutput + CURSOR_PLACEHOLDER
        : resolvedOutput;

    const {
      preparedContent: _output,
      inlinedElements: outputInlinedElements,
      refElements: outputRefElements
    } = prepareContent({
      elements,
      id: message.id,
      content: outputContent,
      language: message.language
    });

    const displayInput = message.input && message.showInput;

    let inputMarkdown;

    if (displayInput) {
      const inputContent =
        message.streaming && message.input
          ? message.input + CURSOR_PLACEHOLDER
          : message.input;
      const { preparedContent: input, refElements: inputRefElements } =
        prepareContent({
          elements,
          id: message.id,
          content: inputContent,
          language:
            typeof message.showInput === 'string'
              ? message.showInput
              : undefined
        });

      inputMarkdown = (
        <>
          <div className="font-medium">Input</div>

          <Markdown
            allowHtml={allowHtml}
            latex={latex}
            refElements={inputRefElements}
          >
            {input}
          </Markdown>
        </>
      );
    }

    const isMessage = message.type.includes('message');

    // Extract sources from metadata (structured citations) or fallback to content parsing
    let sources: string[] = [];
    let citationsData: Array<{title?: string; content?: string; url?: string; marker?: string; index?: number}> = [];
    
    if (message.metadata && typeof message.metadata === 'object') {
      const metadata = message.metadata as Record<string, unknown>;
      const citations = metadata['citations'];
      if (Array.isArray(citations)) {
        citationsData = citations as Array<{title?: string; content?: string; url?: string; marker?: string; index?: number}>;
        sources = citations
          .map((c: any) => c?.title || c?.filepath || '')
          .filter(Boolean);
      }
    }
    
    // Fallback to old behavior if no structured citations in metadata
    if (sources.length === 0) {
      sources = extractSourcesFromContent(resolvedOutput);
    }
    
    const cleanOutput = removeSourcesFromContent(resolvedOutput);
    
    // Only show sources after streaming is complete
    const shouldShowSources = sources.length > 0 && !isStreaming && !isFrontendStreaming;

    return (
      <div className="message-content w-full flex flex-col gap-2">
        {!!inputMarkdown || cleanOutput ? (
          <div className="flex flex-col gap-4">
            {inputMarkdown}
            {cleanOutput ? (
              <>
                {!isMessage && displayInput && message.output ? (
                  <div className="font-medium">Output</div>
                ) : null}
                <Markdown
                  allowHtml={allowHtml}
                  latex={latex}
                  refElements={outputRefElements}
                >
                  {cleanOutput + (isStreaming && enableFrontendStreaming ? CURSOR_PLACEHOLDER : '')}
                </Markdown>
              </>
            ) : null}
          </div>
        ) : null}
        {shouldShowSources ? <SourcesAccordion sources={sources} citations={citationsData} /> : null}
        <InlinedElements elements={outputInlinedElements} />
      </div>
    );
  }
);

export { MessageContent };
